import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@insight/toolkit-utils/lib/labels';
import Button from '../Button/Button';
import ButtonGroup from '../ButtonGroup/ButtonGroup';

export const Pagination = (props) => {
  const { currentPage, totalPages, pageHandler } = props;
  const singlePage = totalPages <= 1;
  const setPage = (desiredPage) => {
    if (currentPage != desiredPage) pageHandler(desiredPage);
  };

  const renderPageButtons = (start, end) =>
    [...Array.from(Array(end - start + 1))].map((_, i) => {
      const pageNumber = start + i;

      return (
        <Button
          key={pageNumber}
          className="c-pagination__box"
          color={pageNumber === currentPage ? 'primary' : 'secondary'}
          onClick={() => setPage(pageNumber)}
          size="small"
          aria-label={`${t('Go to page')} ${pageNumber}`}
        >
          <span className="c-pagination__page-number">{pageNumber}</span>
        </Button>
      );
    });

  const padSize = 2; // The number of pages you want to show either side of the current page.
  let pageStart = 0;
  let pageEnd = 0;

  if (currentPage <= padSize) {
    pageStart = 2;
    pageEnd = padSize * 2 + 1;
  } else if (currentPage > totalPages - padSize) {
    pageStart = totalPages - padSize * 2;
    pageEnd = totalPages - 1;
  } else {
    pageStart = currentPage - padSize;
    pageEnd = currentPage + padSize;
  }

  if (pageEnd >= totalPages) {
    pageEnd = totalPages - 1;
  }
  if (pageStart < 2) {
    pageStart = 2;
  }

  const startGap = pageStart - 1;
  const endGap = totalPages - pageEnd;
  const midpointThreshold = 5;
  const showMidPoints =
    startGap > midpointThreshold && endGap > midpointThreshold;
  const startMidpoint = Math.floor(startGap / 2) + 1;
  const endMidpoint = totalPages - Math.floor(endGap / 2);

  const renderMidpoint = (isStart, midpoint) => {
    const gapCondition = isStart ? startGap > 3 : endGap > 3;
    return showMidPoints && gapCondition ? (
      <>
        <span className="c-pagination__ellipses">...</span>
        <Button
          className="c-pagination__box u-show@tablet"
          color="secondary"
          onClick={() => setPage(midpoint)}
          size="small"
          aria-label={`${t('Go to page')} ${midpoint}`}
        >
          <span className="c-pagination__page-number">{midpoint}</span>
        </Button>
        <span className="c-pagination__ellipses u-show@tablet">...</span>
      </>
    ) : (
      <span className="c-pagination__ellipses">...</span>
    );
  };

  return (
    <section
      className="c-pagination"
      aria-label={`${t('Page Navigation.')} ${
        totalPages
      } ${t('total pages. Currently on page')} ${currentPage}`}
    >
      <ButtonGroup align="center" className="c-pagination__group">
        <Button
          className="c-pagination__box c-pagination__prev u-show@tablet"
          color="secondary"
          icon="arrow-left"
          isDisabled={currentPage <= 1}
          onClick={() => {
            setPage(currentPage - 1);
          }}
          size="small"
          aria-label={t('Go to previous page')}
          data-testid="pagination-prev"
        />
        <Button
          className="c-pagination__box"
          color={currentPage === 1 ? 'primary' : 'secondary'}
          onClick={() => {
            setPage(1);
          }}
          size="small"
          aria-label={`${t('Go to page')} 1`}
        >
          <span className="c-pagination__page-number">{1}</span>
        </Button>
        {startGap > 1 && renderMidpoint(true, startMidpoint)}
        {!singlePage && renderPageButtons(pageStart, pageEnd)}
        {endGap > 1 && renderMidpoint(false, endMidpoint)}
        {!singlePage && (
          <Button
            className="c-pagination__box"
            color={currentPage === totalPages ? 'primary' : 'secondary'}
            onClick={() => {
              setPage(totalPages);
            }}
            size="small"
            aria-label={`${t('Go to page')} ${totalPages}`}
          >
            <span className="c-pagination__page-number">{totalPages}</span>
          </Button>
        )}
        <Button
          className="c-pagination__box c-pagination__next u-show@tablet"
          color="secondary"
          icon="arrow-right"
          isDisabled={currentPage >= totalPages}
          onClick={() => setPage(currentPage + 1)}
          size="small"
          aria-label={t('Go to next page')}
          data-testid="pagination-next"
        />
      </ButtonGroup>

      <ButtonGroup
        align="center"
        className="c-pagination--mobile u-hide@tablet"
        data-testid="pagination-mobile"
      >
        <Button
          className="c-pagination__box c-pagination__prev"
          color="secondary"
          icon="arrow-left"
          isDisabled={currentPage <= 1}
          onClick={() => {
            setPage(currentPage - 1);
          }}
          size="small"
          aria-label={t('Go to previous page')}
          data-testid="pagination-mobile-prev"
        />

        <Button
          className="c-pagination__box c-pagination__next"
          color="secondary"
          icon="arrow-right"
          isDisabled={currentPage >= totalPages}
          onClick={() => setPage(currentPage + 1)}
          size="small"
          aria-label={t('Go to next page')}
          data-testid="pagination-mobile-next"
        />
      </ButtonGroup>
    </section>
  );
};

export default Pagination;

Pagination.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  pageHandler: PropTypes.func,
};
